/**
 * SessionQuotaBlockedDialog — modale affichée quand l'utilisateur tente
 * de créer une session au-delà de son quota.
 *
 * Différencié selon l'environnement et le profil :
 *   - DEMO : CTA "S'abonner" → /subscribe
 *   - PROD/staging, acheteurs (`canBuyLicences` : ORG, ADM, GES) : CTA "Acheter un pack"
 *     → `ACHAT_PACK_URL` (« Mon compte › Licences et Packs », la même destination partout)
 *   - PROD/staging, autres profils (BNF, MNG, RH, COO…) : pas de CTA d'achat, juste un message
 *     les invitant à contacter l'organisateur ou le gestionnaire financier du compte.
 *
 * Cf. spec/rate-limiter-sessions.md §1.9
 */
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { ShieldAlert, ShoppingCart, Sparkles, Mail } from "lucide-react";
import type { SubscriptionState } from "../lib/subscription-access";
import { formatDateAbonnement } from "../lib/subscription-labels";
import { canBuyLicences, ACHAT_PACK_URL, REABONNEMENT_URL } from "../lib/encadrant";

export interface QuotaBlockedInfo {
  used: number;
  limit: number;
  totalPacksRemaining: number;
  ctaUrl?: string;
  envMode?: "prod" | "demo" | "staging";
  cycleStart?: string; // ISO
  cycleEnd?: string; // ISO
  /**
   * Profil de l'utilisateur bloqué — sert à différencier l'UI (règle `canBuyLicences`) :
   * - acheteur (ORG / ADM / GES) → CTA achat de pack
   * - autre profil → message invitant à contacter l'organisateur ou le gestionnaire financier
   */
  userProfileType?: string | null;
  /** Motif exact du refus, seul juge du message affiché (cf. `finAbonnement` plus bas). */
  reason?: "demo_quota_exceeded" | "monthly_quota_exceeded" | "no_active_subscription" | "subscription_ended";
  /**
   * État de l'abonnement quand le blocage vient de LUI et non du quota mensuel : la modale
   * annonçait « 0 sessions sur les 0 incluses » à un client dont l'abonnement avait pris fin,
   * puis l'envoyait vers un achat de pack que le serveur refusait en 400.
   */
  subscriptionState?: SubscriptionState;
  subscriptionEndedAt?: string | null;
  subscriptionResumesAt?: string | null;
  /** `"manual"` : licence d'abonnement posée à la main par Kestio, arrivée à expiration. */
  subscriptionSource?: "manual";
}

interface Props {
  info: QuotaBlockedInfo | null;
  onClose: () => void;
  onCta: (url: string) => void;
}

export function SessionQuotaBlockedDialog({ info, onClose, onCta }: Props) {
  const { t, i18n } = useTranslation();
  if (!info) return null;

  // Licence d'abonnement posée à la main, expirée : son renouvellement se négocie avec Kestio, hors
  // ligne (décision de Valérie du 23/10/2026). Vaut AUSSI sur demo (Q1 de l'audit du 23/10) : ce
  // n'est pas un prospect en découverte, le message « 10 sessions de découverte » serait faux.
  const licenceManuelle = info.reason === "subscription_ended" && info.subscriptionSource === "manual";
  // « Démo » = la règle de découverte des prospects, pas l'environnement en tant que tel.
  const isDemo = info.envMode === "demo" && !licenceManuelle;
  // Abonnement terminé ou en pause : ce n'est pas un dépassement de quota, le message et le
  // bouton diffèrent. L'achat de pack interne passe par l'add-on Zoho de l'abonnement, qui
  // n'existe plus une fois la période close → on renvoie vers la page de paiement publique.
  // ⚠️ Le MOTIF décide, pas l'état : l'état d'abonnement accompagne TOUS les refus, y compris un
  // simple dépassement de quota. S'y fier annonçait « votre abonnement a pris fin le <date
  // FUTURE> » à un client résilié encore dans sa période qui avait juste épuisé ses 30 sessions,
  // et lui retirait le bouton d'achat de pack que le serveur autorise pourtant dans ce cas.
  const finAbonnement = !isDemo && info.reason === "subscription_ended";
  // Source unique, partagée avec l'étiquette, le bandeau et l'écran des exercices.
  const formatDate = (v?: string | null) => formatDateAbonnement(v, i18n.language);
  const finLabel = formatDate(info.subscriptionEndedAt);
  const repriseLabel = formatDate(info.subscriptionResumesAt);
  // Règle PARTAGÉE avec le serveur (`canBuyLicences` : ORG, ADM, GES — Q3 du 29-30/09/2026). La
  // copie locale (ORG/ADM/COO) montrait au COO un bouton que le serveur refusait ; et un profil
  // inconnu valait « ORG » par défaut — jamais de bouton d'achat deviné.
  const canPurchase = isDemo || canBuyLicences(info.userProfileType);

  // Une PAUSE se termine d'elle-même : proposer un achat exposerait à une double facturation
  // (arbitrage Valérie, 29/09/2026). On oriente vers Kestio, qui décide de la lever ou non.
  const enPause = finAbonnement && info.subscriptionState === "paused";
  // « S'abonner » ferait souscrire un abonnement en ligne en plus du contrat : même bouton que la pause.
  const contacterKestio = enPause || licenceManuelle;
  const ctaLabel = contacterKestio
    ? t("quota.ctaContactKestio")
    : isDemo || finAbonnement
      ? t("quota.ctaSubscribe")
      : t("quota.ctaBuyPack");
  const ctaIcon = contacterKestio
    ? <Mail className="h-4 w-4 mr-2" />
    : isDemo || finAbonnement
      ? <Sparkles className="h-4 w-4 mr-2" />
      : <ShoppingCart className="h-4 w-4 mr-2" />;
  // ⚠️ `/subscribe` N'EXISTE PAS (ni dans `App.tsx`, ni dans `server.ts` qui ne route que
  // `/buy/:slug`) : le client retombait sur l'accueil. Un client EXISTANT ne doit pas non plus
  // être renvoyé vers `/signup`, qui lui ferait créer un second compte. On l'envoie sur le
  // parcours d'achat d'abonnement déjà utilisé par les clients actifs (arbitrage Valérie,
  // 29/09/2026) : onglet « Licences et Packs », modale d'achat ouverte (`buy=1`), qui passe par
  // `POST /api/zoho/buy-again` et réutilise son client Zoho — donc aucun doublon.
  // La découverte DEMO garde son ancien lien, défaut antérieur relevant d'un autre lot.
  // « Acheter un pack » mène à la MÊME destination partout (`ACHAT_PACK_URL`, correction n°5 du
  // 30/09/2026) : l'ancienne adresse `/account/buy-pack` était inconnue du routeur (retour accueil).
  const ctaUrl = contacterKestio
    ? "mailto:contact@kestio.com"
    : finAbonnement
      ? REABONNEMENT_URL
      : isDemo ? (info.ctaUrl || "/subscribe") : ACHAT_PACK_URL;

  // ⚠️ Était figé sur « fr-FR » : la date de renouvellement s'affichait en français même en
  // anglais. Elle passe par la même mise en forme que les autres dates de l'abonnement.
  const cycleEndLabel = formatDate(info.cycleEnd);

  return (
    <Dialog open={!!info} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-[480px]" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
              <ShieldAlert className="h-5 w-5 text-amber-600" />
            </div>
            <DialogTitle>
              {isDemo
                ? t("quota.demoTitle")
                : finAbonnement
                  ? info.subscriptionState === "paused"
                    ? t("quota.pausedTitle")
                    : licenceManuelle ? t("quota.accessEndedTitle") : t("quota.endedTitle")
                  : t("quota.monthlyTitle")}
            </DialogTitle>
          </div>
          <DialogDescription className="pt-3 space-y-2">
            <p>
              {isDemo
                ? t("quota.demoBody")
                : finAbonnement
                ? (info.subscriptionState === "paused"
                    ? (repriseLabel ? t("quota.pausedUntil", { date: repriseLabel }) : t("quota.pausedNoDate"))
                    : licenceManuelle
                      ? (finLabel ? t("quota.accessEndedOn", { date: finLabel }) : t("quota.accessEndedNoDate"))
                      : (finLabel ? t("quota.endedOn", { date: finLabel }) : t("quota.endedNoDate")))
                : info.totalPacksRemaining > 0
                  ? t("quota.usedOfLimitWithPacks", { used: info.used, limit: info.limit, count: info.totalPacksRemaining })
                  : t("quota.usedOfLimit", { used: info.used, limit: info.limit })}
            </p>
            {!isDemo && !finAbonnement && cycleEndLabel && (
              <p className="text-sm text-muted-foreground">
                {t("quota.renewsOn", { date: cycleEndLabel })}{" "}
                {canPurchase ? t("quota.renewBuyerHint") : t("quota.renewNonBuyerHint")}
              </p>
            )}
            {!isDemo && !canPurchase && (
              <div className="mt-3 flex items-start gap-2 rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
                <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  {/* Abonnement clos : parler d'un « pack supplémentaire » enverrait le
                      collaborateur demander une chose impossible — c'est un réabonnement
                      qu'il faut. Deux situations, deux consignes. */}
                  {licenceManuelle
                    ? t("quota.askOrgToContactKestio")
                    : finAbonnement
                    ? t("quota.askOrgToResubscribe")
                    : t("quota.askBuyerForPack")}
                </span>
              </div>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant={canPurchase ? "outline" : "default"} onClick={onClose}>
            {canPurchase ? t("quota.later") : t("quota.ok")}
          </Button>
          {canPurchase && (
            <Button onClick={() => onCta(ctaUrl)}>
              {ctaIcon}
              {ctaLabel}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
